import React, { useState } from 'react'

import { TAnalysis } from 'redux/hooks/Analyses'
import { getParsedDate } from 'utils/dateParser'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'
import Text from 'components_new/atoms/Text'

import SidebarDialog from 'components_new/molecules/SidebarDialog'

interface AnalysisExcludedProps {
  analysis: TAnalysis
}

// Stands in for the date, so the sentence around it stays one translation.
const DATE_MARK = '\u0000'

/**
 * The properties the run left out: those the analysis type excluded when
 * it was made, and those whose name more than one real estate goes by.
 * Later changes to the list apply to coming runs and are not shown here.
 */
const AnalysisExcluded = (props: AnalysisExcludedProps) => {
  const { analysis } = props

  const { lang, t } = useTranslation()
  const [open, setOpen] = useState(false)

  const excluded = analysis.meta?.excluded_real_estates ?? []
  const ambiguous = analysis.meta?.ambiguous_real_estates ?? []

  if (excluded.length + ambiguous.length === 0) return null

  const getCount = (length: number) =>
    length === 1
      ? t('analyses', 'excluded_count_one')
      : t('analyses', 'excluded_count', false, { count: String(length) })

  const [before, after] = t('analyses', 'excluded_dialog_body', false, {
    date: DATE_MARK
  }).split(DATE_MARK)

  const chipListSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.75,
    maxHeight: 186,
    overflowY: 'auto'
  }

  return (
    <>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        <Link
          onClick={() => setOpen(true)}
          sx={{ textDecorationStyle: 'dotted' }}
          underline="always"
        >
          {getCount(excluded.length + ambiguous.length)}
        </Link>
        {ambiguous.length > 0 ? (
          <Icon color="warning" fontSize="inherit" name="Warning" />
        ) : null}
      </Box>

      <SidebarDialog
        actions={
          <Button onClick={() => setOpen(false)} variant="text">
            {t('global', 'close')}
          </Button>
        }
        maxWidth="xs"
        onClose={() => setOpen(false)}
        open={open}
        title={t('analyses', 'excluded_dialog_title')}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {excluded.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Text color="text.secondary" variant="body1">
                {before}
                <Text
                  color="text.primary"
                  component="span"
                  fontWeight="fontWeightBold"
                  sx={{ whiteSpace: 'nowrap' }}
                  variant="body1"
                >
                  {getParsedDate(analysis.created_at, lang, { time: 'full' })}
                </Text>
                {after}
              </Text>
              <Box>
                <Text color="text.secondary" sx={{ mb: 1 }} variant="body2">
                  {getCount(excluded.length)}
                </Text>
                <Box sx={chipListSx}>
                  {excluded.map((name) => (
                    <Chip key={name} label={name} size="small" />
                  ))}
                </Box>
              </Box>
            </Box>
          ) : null}

          {ambiguous.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                <Icon color="warning" fontSize="small" name="Warning" />
                <Text fontWeight="fontWeightBold" variant="body1">
                  {t('analyses', 'ambiguous_title')}
                </Text>
              </Box>
              <Text color="text.secondary" variant="body1">
                {t('analyses', 'ambiguous_dialog_body')}
              </Text>
              <Box sx={chipListSx}>
                {ambiguous.map((name) => (
                  <Chip key={name} label={name} size="small" />
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>
      </SidebarDialog>
    </>
  )
}

export default AnalysisExcluded
